import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as React from 'react';
import { IconName } from '../images/IconMap';
import Icons from '../images/Icons';
import { LightXSmall, TextSize80, TitleXSmall } from '../text/Typography';

interface ITinyHeaderProps {
    title: string;
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'; // default is h3
    description?: string;
    children?: React.ReactNode;
    prefixElement?: React.ReactNode;
    icon?: IconName;
    rail?: boolean;
    /** 'small' sets the title in uppercase eyebrow type, for headings inside a card or dialog. */
    size?: 'medium' | 'small';
}

export default function TinyHeader({
    title,
    description,
    children,
    prefixElement,
    icon,
    rail,
    as,
    size = 'medium',
}: Readonly<ITinyHeaderProps>) {
    return (
        <RowWrapper $rail={rail}>
            <TextHeadingWrapper>
                <Column>
                    <Row>
                        {prefixElement}
                        {icon && <Icons name={icon} size={16} />}
                        <Title as={as ?? 'h3'} $small={size === 'small'}>
                            {title}
                        </Title>
                    </Row>
                    {description && <LightXSmall>{description}</LightXSmall>}
                </Column>
            </TextHeadingWrapper>
            {children}
        </RowWrapper>
    );
}

/** The small size reads as an eyebrow: smaller, uppercase, and tracked out so it still holds its own. */
const Title = styled(TitleXSmall)<{ $small?: boolean }>`
    ${(props) =>
        props.$small &&
        css`
            ${TextSize80}
            text-transform: uppercase;
            letter-spacing: 0.04em;
            font-weight: 700;
        `}
`;

const RowWrapper = styled.div<{ $rail?: boolean }>`
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    justify-content: space-between;
    padding: 1rem 0 0.25rem 0;
    ${(props) =>
        props.$rail &&
        `
        border-bottom: 1px solid ${props.theme.color.cardHover};
    `}
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
`;

const TextHeadingWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
`;
